import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useTranslation, withTranslation } from "react-i18next";

import { AgGridReact } from "ag-grid-react";
import { red } from '@mui/material/colors'

import { Box, Button, Checkbox, Divider, FormControl, FormControlLabel, FormGroup, IconButton, InputAdornment, Popover, Tooltip } from "@mui/material";
import TextField from "@mui/material/TextField";
import { ArrowDropDown, ArrowDropUp, Clear, DoneAll } from "@mui/icons-material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3'
import ShareLocationIcon from '@mui/icons-material/ShareLocation';
import LocationOnIcon from '@mui/icons-material/LocationOn';

import supplyInventoryService from "@plannel/services/ip/supply-inventory-service";
import siteService from "@plannel/services/data/site-master-service";

import PaginationContainer from "@plannel/components/PaginationContainer";
import { IPItemAutocomplete } from "@plannel/components/filter/ItemAutocomplete";
import reduxUtil from "@plannel/utils/redux-util";
import debug from "@plannel/utils/debug-util";
import i18n from "@plannel/utils/i18n";
import GridUtils from "@plannel/components/aggrid/GridUtils";
import DefaultGridSetting from "@plannel/components/aggrid/DefaultGridSetting";
import ExcelExportButton from "@plannel/components/ExcelExportButton"
import Snackbar from "@plannel/components/Snackbar";
import ColorUtil from "@plannel/pages/utils/color-util";
import CodeUtil from "@plannel/pages/utils/code-util";
import FilterContainer from "@plannel/components/layout/FilterContainer";
import ItemHrchyTreeFilter from "@plannel/components/filter/ItemHrchyTreeFilter";
import { IPLocationTreeFilter } from "@plannel/components/filter/LocationTreeFilter";
import dateUtils from "@plannel/utils/date-util";
import { cloneDeep, isNull, isUndefined, set, size, replace, isEmpty, isNil } from "lodash";
import numberUtil from "@plannel/utils/number-util";
import InvCovDaysSettings from "@plannel/components/modal/InvCovDaysSettings";
import { SettingsButton } from "@plannel/components/ActionIconButton";

const AVG_SALES_QTY_PIVOT = "avgSalesQtyPivot";
const AVG_SALES_AMT_PIVOT = "avgSalesAmtPivot";
const INTRANSIT_QTY_PIVOT = "intransitQtyPivot";
const INTRANSIT_AMT_PIVOT = "intransitAmtPivot";
const DAYS_OF_SUPPLY_BY_QTY_PIVOT = "daysOfSupplyByQtyPivot";
const STOCKOUT_DATE_PIVOT = "stockoutDatePivot";
const DAYS_OF_SUPPLY_WITH_TRANSIT_BY_QTY_PIVOT = "daysOfSupplyWithTransitByQtyPivot";
const STOCKOUT_DATE_WITH_TRANSIT_PIVOT = "stockoutDateWithTransitPivot";
const YEAR = "year";
const MONTH = "month";
const DAY = "day";
const YYYYMMDD = "yyyy-MM-dd";
const EXCEL_EXPORT = "excelExport";
const { getCurrencySymbol, inventoryCoverageDaysPolicy } = CodeUtil;
const { INV_COV_DAYS_THRESHOLD, INV_COV_DAYS_APPLY, INV_COV_DAYS_COMPUTED_METHOD } = inventoryCoverageDaysPolicy;

const EXTEND_HEADER_KEY = {
  [AVG_SALES_QTY_PIVOT]: "avgSalesQty",
  [AVG_SALES_AMT_PIVOT]: "avgSalesAmt",
  [INTRANSIT_QTY_PIVOT]: "intransitQty",
  [INTRANSIT_AMT_PIVOT]: "intransitAmt",
  [DAYS_OF_SUPPLY_BY_QTY_PIVOT]: "daysOfSupplyByQty",
  [STOCKOUT_DATE_PIVOT]: "stockoutDate",
  [DAYS_OF_SUPPLY_WITH_TRANSIT_BY_QTY_PIVOT]: "daysOfSupplyWithTransitByQty",
  [STOCKOUT_DATE_WITH_TRANSIT_PIVOT]: "stockoutDateWithTransit",
}

const CURRENT_DATE = new ZDate();
const END_DATE_MAX = new ZDate(CURRENT_DATE.getFullYear(), CURRENT_DATE.getMonth(), CURRENT_DATE.getDate() - 1, 23, 59, 59);
const { snackBarMessageColor } = ColorUtil;
const SupplyInventory = ({ t, viewName, title }) => {
  const reduxViewState = reduxUtil.getViewState(viewName);
  const reduxDispatch = useDispatch();

  const [invCovModalOpen, setInvCovModalOpen] = useState(false);
  const [invCovDaysConfig, setInvCovDaysConfig] = useState({});
  const [isSiteUsed, setIsSiteUsed] = useState(false);
  const [isSiteGroup, setIsSiteGroup] = useState(reduxViewState?.isSiteGroup ?? false);
  const [filterValue, setFilterValue] = useState(reduxViewState?.filter ?? { locations: null, category: null });
  const [periodList, setPeriodList] = useState(reduxViewState?.periodList ?? [7]);
  const [periodEndDate, setPeriodEndDate] = useState(reduxViewState?.periodEndDate ?
    new ZDate(reduxViewState.periodEndDate) :
    new ZDate(CURRENT_DATE.getFullYear(), CURRENT_DATE.getMonth(), CURRENT_DATE.getDate() -1, 0, 0, 0)
  );
  const itemSearch = useRef(reduxViewState?.itemSearch ?? "");
  const extraItemCodesRef = useRef(reduxViewState?.extraItemCodes ?? []);

  const pageSizes = [30, 100, 200];
  const [currentPage, setCurrentPage] = useState(reduxViewState?.currentPage ? reduxViewState.currentPage : 1);
  const [tPages, setTotalPages] = useState(0);
  const [pageSize, setPageSize] = useState(reduxViewState?.pageSize ? reduxViewState.pageSize : pageSizes[1]);
  const resultTotalCount = useRef(null);

  const [columnDefs, setColumnDefs] = useState([]);
  const [csvColDef, setCsvColDef] = useState([]);
  const [invDaysOfsupply, setInvDaysOfsupply] = useState([]);
  const gridRef = useRef();
  const [gridLoading, setGridLoading] = useState(false);
  const [sortParams, setSortParams] = useState(reduxViewState?.sortParams ?? {});

  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });
  const userData = JSON.parse(localStorage.getItem("user"));
  const currencyMap = useRef({});
  const defaultGridMemo = useMemo(() => {
    return DefaultGridSetting({ title, viewName });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const makeFieldName = (key, period) => {
    return `${EXTEND_HEADER_KEY[key]}${period}`;
  }

  const suffixDayFormatter = (params) => {
    const suffixDay = ` ${t("dayUnit")}`;
    if (isNull(params?.value) || isUndefined(params?.value) || !isFinite(params?.value)) {
      return "";
    }
    return numberUtil.formatNumberData(params, { minimumFractionDigits: 0, maximumFractionDigits: 0 }) + suffixDay;
  }

  const openInvCovDaysModal = () => {
    setInvCovModalOpen(true);
  }

  const cellStyleCautionTarget = (params) => {
    const { value } = params;
    const style = {};

    if (isNil(value)) {
      return style;
    }

    let isCaution = false;
    if (!isEmpty(invCovDaysConfig) && invCovDaysConfig[INV_COV_DAYS_APPLY]) {
      if (invCovDaysConfig[INV_COV_DAYS_COMPUTED_METHOD] === "lessThan") {
        isCaution = value < invCovDaysConfig[INV_COV_DAYS_THRESHOLD];
      } else {
        isCaution = value > invCovDaysConfig[INV_COV_DAYS_THRESHOLD];
      }
    }

    if (isCaution) {
      style.background = red[100];
    }

    return style;
  }

  const currencyRenderer = (param, isExport = false) => {
    let siteOrLocCd = param?.data?.locCd;
    if (isSiteGroup) {
      siteOrLocCd = param?.data?.siteCd;
    }
    let symbol = userData?.currencySymbol;
    if (size(Object.keys(currencyMap?.current))) {
      const currencyCd = currencyMap.current[siteOrLocCd];
      if (currencyCd) {
        symbol = getCurrencySymbol(currencyCd);
      }
    }

    const value = param?.data?.[param?.colDef?.field];
    if (isNull(value) || isUndefined(value)) {
      return "";
    }
    const formatValue = numberUtil.formatNumber(value, { maximumFractionDigits: 2, minimumFractionDigits: 2 });
    if (isExport) {
      return formatValue.replace(/,/g, "");
    }
    if (value < 0) {
      return `-${symbol}${replace(formatValue, "-", "")}`;
    }
    return symbol + formatValue;
  };
  currencyRenderer.customType = EXCEL_EXPORT;

  const makeExtendHeader = useCallback(() => {
    const extendHeader = [];

    for (const analysisPeriod of periodList) {
      extendHeader.push({
        ...GridUtils.composedHeader(() => i18n.t("nDayAnalysis", { N: analysisPeriod })),
        groupId: `salesHistory${analysisPeriod}`,
        children: [
          { headerName: "dailyAvgSalesQty", field: makeFieldName(AVG_SALES_QTY_PIVOT, analysisPeriod), type: ["rationalColumn", "nonEditableColumn", "rightAligned"], width: 100 },
          { headerName: "dailyAvgSalesAmt", field: makeFieldName(AVG_SALES_AMT_PIVOT, analysisPeriod), type: ["numberColumn", "nonEditableColumn", "rightAligned"], valueFormatter: currencyRenderer, width: 100 },
          {
            headerName: "daysOfSupply", field: makeFieldName(DAYS_OF_SUPPLY_BY_QTY_PIVOT, analysisPeriod),
            type: ["rationalColumn", "nonEditableColumn", "rightAligned"],
            valueFormatter: suffixDayFormatter,
            cellStyle: (params) => cellStyleCautionTarget(params),
            width: 100
          },
          { headerName: "stockoutDate", field: makeFieldName(STOCKOUT_DATE_PIVOT, analysisPeriod), type: ["nonEditableColumn"], valueFormatter: (e) => dateUtils.formatDate(e.value, "yyyy-MM-dd"), width: 130 },
          { headerName: "intransitQty", field: makeFieldName(INTRANSIT_QTY_PIVOT, analysisPeriod), type: ["rationalColumn", "nonEditableColumn", "rightAligned"], width: 110 },
          { headerName: "intransitAmt", field: makeFieldName(INTRANSIT_AMT_PIVOT, analysisPeriod), type: ["numberColumn", "nonEditableColumn", "rightAligned"], valueFormatter: currencyRenderer, width: 110 },
          {
            ...GridUtils.composedHeader(() => `${i18n.t("daysOfSupply")} (+${i18n.t("intransitInventory")})`),
            field: makeFieldName(DAYS_OF_SUPPLY_WITH_TRANSIT_BY_QTY_PIVOT, analysisPeriod),
            type: ["rationalColumn", "nonEditableColumn", "rightAligned"],
            valueFormatter: suffixDayFormatter,
            cellStyle: (params) => cellStyleCautionTarget(params),
            width: 110
          },
          {
            ...GridUtils.composedHeader(() => `${i18n.t("stockoutDate")} (+${i18n.t("intransitInventory")})`),
            field: makeFieldName(STOCKOUT_DATE_WITH_TRANSIT_PIVOT, analysisPeriod), type: ["nonEditableColumn"],
            valueFormatter: (e) => dateUtils.formatDate(e.value, "yyyy-MM-dd"), width: 130
          },
        ]
      });
    }

    return extendHeader;

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periodList, invCovDaysConfig, isSiteGroup]);

  const defaultColumnDefs = [
      { headerName: "locCd", field: "locCd", type: ["nonEditableColumn"], width: 180, pinned: "left", lockPinned: true, hide: true },
      { headerName: "locName", field: "locName", type: ["nonEditableColumn"], width: 180, pinned: "left", lockPinned: true },
      { headerName: "itemCategory", field: "itemHrchyName", type: ["nonEditableColumn"], width: 180, pinned: "left", lockPinned: true },
      { headerName: "itemCd", field: "itemCd", type: ["nonEditableColumn"], width: 180, pinned: "left", lockPinned: true },
      { headerName: "itemName", field: "itemName", type: ["nonEditableColumn"], width: 180, pinned: "left", lockPinned: true },
      {
        headerName: "onhand", groupId: "onHand",
        children: [
          { headerName: "qty", field: "onHandQty", type: ["numberColumn", "nonEditableColumn", "rightAligned"], width: 100 },
          { headerName: "amt", field: "onHandAmt", type: ["numberColumn", "nonEditableColumn", "rightAligned"], valueFormatter: currencyRenderer, width: 110 },
        ]
      }
    ];

  // groupCombined Site
  const siteColumnDefs = [
      { headerName: "siteCd", field: "siteCd", type: ["nonEditableColumn"], width: 180, pinned: "left", lockPinned: true, hide: true },
      { headerName: "siteName", field: "siteName", type: ["nonEditableColumn"], width: 180, pinned: "left", lockPinned: true },
      { headerName: "itemCategory", field: "itemHrchyName", type: ["nonEditableColumn"], width: 180, pinned: "left", lockPinned: true },
      { headerName: "itemCd", field: "itemCd", type: ["nonEditableColumn"], width: 180, pinned: "left", lockPinned: true },
      { headerName: "itemName", field: "itemName", type: ["nonEditableColumn"], width: 180, pinned: "left", lockPinned: true },
      {
        headerName: "onhand", groupId: "onHand",
        children: [
          { headerName: "qty", field: "onHandQty", type: ["numberColumn", "nonEditableColumn", "rightAligned"], width: 100 },
          { headerName: "amt", field: "onHandAmt", type: ["numberColumn", "nonEditableColumn", "rightAligned"], valueFormatter: currencyRenderer, width: 110 },
        ]
      }
    ];

  const onGridReady = params => {
    setGridLoading(true);
    GridUtils.setColumnDefs({ ...params, columnDefs, viewName, initState: true });
  };

  const siteGroupHandler = () => {
    setIsSiteGroup(!isSiteGroup);
  }

  const getRequestParams = () => {
    const params = {};

    if (filterValue?.category) {
      params.itemHrchyCdList = filterValue?.category?.filter((category) => category.leafFlg).map((category) => category.hrchyCd);
    }

    if (filterValue?.locations) {
      params.locCdList = filterValue?.locations?.map(location => location?.locCd);
    }

    if (extraItemCodesRef.current?.length > 0) {
      params.items = [itemSearch.current, ...extraItemCodesRef.current].filter(Boolean);
    } else if (itemSearch.current) {
      params.item = itemSearch.current;
    }

    if (currentPage) {
      params.page = currentPage - 1;
    }

    if (pageSize) {
      params.pageSize = pageSize;
    }

    if (resultTotalCount.current) {
      params.totalCount = resultTotalCount.current;
    }

    if (periodList.length > 0) {
      params.periodList = periodList;
    }

    if (periodEndDate) {
      params.periodEndDate = periodEndDate;
    }

    params.siteFlg = isSiteGroup;
    params.nowDate = CURRENT_DATE;

    return { ...params, ...sortParams };
  };

  const setGridHeaders = (siteOnlyParam) => {
    let colDefs = [];

    if (siteOnlyParam) {
      colDefs = cloneDeep(siteColumnDefs);
    } else {
      colDefs = cloneDeep(defaultColumnDefs);
    }
    // const exportColDefs = cloneDeep(defaultColumnDefs);

    const extendHeader = makeExtendHeader();
    colDefs.push(...extendHeader);

    setColumnDefs(colDefs);
  }

  const setGridDatas = (results) => {
    const dataList = [];

    for (const result of results) {
      const data = { ...result };

      for (let idx = 0; idx < periodList.length; idx++) {
        const analysisPeriod = periodList[idx];

        const avgSalesQty = result[AVG_SALES_QTY_PIVOT][idx];
        const avgSalesAmt = result[AVG_SALES_AMT_PIVOT][idx];
        const intransitQty = result[INTRANSIT_QTY_PIVOT][idx];
        const intransitAmt = result[INTRANSIT_AMT_PIVOT][idx];
        const daysOfSupplyByQty = result[DAYS_OF_SUPPLY_BY_QTY_PIVOT][idx];
        const stockoutDate = result[STOCKOUT_DATE_PIVOT][idx];
        const daysOfSupplyWithTransitByQty = result[DAYS_OF_SUPPLY_WITH_TRANSIT_BY_QTY_PIVOT][idx];
        const stockoutDateWithTransit = result[STOCKOUT_DATE_WITH_TRANSIT_PIVOT][idx];

        set(data, makeFieldName(AVG_SALES_QTY_PIVOT, analysisPeriod), avgSalesQty);
        set(data, makeFieldName(AVG_SALES_AMT_PIVOT, analysisPeriod), avgSalesAmt);
        set(data, makeFieldName(INTRANSIT_QTY_PIVOT, analysisPeriod), intransitQty);
        set(data, makeFieldName(INTRANSIT_AMT_PIVOT, analysisPeriod), intransitAmt);
        set(data, makeFieldName(DAYS_OF_SUPPLY_BY_QTY_PIVOT, analysisPeriod), daysOfSupplyByQty);
        set(data, makeFieldName(STOCKOUT_DATE_PIVOT, analysisPeriod), stockoutDate);
        set(data, makeFieldName(DAYS_OF_SUPPLY_WITH_TRANSIT_BY_QTY_PIVOT, analysisPeriod), daysOfSupplyWithTransitByQty);
        set(data, makeFieldName(STOCKOUT_DATE_WITH_TRANSIT_PIVOT, analysisPeriod), stockoutDateWithTransit);
      }

      dataList.push(data);
    }

    return dataList;
  }

  const retireveSiteUsed = () => {
    siteService.getSiteUsed()
      .then(response => {
        const { data } = response;

        setIsSiteUsed(Boolean(data));
      })
      .catch(e => {
        debug(e);
      });
  }

  const retrieveSupplyInventory = () => {
    const params = getRequestParams();

    gridRef.current?.api?.setGridOption('loading', true);
    supplyInventoryService.getAll(params)
      .then(response => {
        const { results, totalPages, totalItems, currency } = response.data;
        setTotalPages(totalPages);
        setGridHeaders(isSiteGroup);

        if (results?.length > 0) {
          const convertResults = setGridDatas(results);
          setInvDaysOfsupply(convertResults);
        } else {
          setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoData") });
          setInvDaysOfsupply([]);
        }
        if (size(currency)) {
          currencyMap.current = currency;
        }
        resultTotalCount.current = totalItems;
        const pageState = reduxUtil.createObjectState(viewName, { itemSearch: itemSearch.current, extraItemCodes: extraItemCodesRef.current, currentPage, pageSize, periodList, periodEndDate, isSiteGroup, sortParams});
        const filterState = reduxUtil.createFilterViewState(viewName, filterValue);
        const reduxState = { ...pageState, ...filterState };

        reduxDispatch(reduxUtil.updateViewState(reduxState));
      })
      .catch(e => {
        const { response } = e;
        if (response?.data?.message) {
          setSnackInfo({ open: true, severity: snackBarMessageColor.warning, content: t(response.data.message) });
        } else {
          setSnackInfo({ open: true, severity: snackBarMessageColor.networkError, content: t("MSG_NetworkError") });
        }
        debug(e);
      })
      .finally(() => {
        gridRef.current?.api?.setGridOption('loading', false);
      })
  };

  const initPageToRetrieve = () => {
    resultTotalCount.current = null;
    if (currentPage > 1) {
      setCurrentPage(1);
    } else {
      retrieveSupplyInventory();
    }
  }

  const onAcceptHandler = (dateValue) => {
    const convertDate = new ZDate(dateValue.getFullYear(), dateValue.getMonth(), dateValue.getDate(), 23, 59, 59);

    setPeriodEndDate(convertDate);
  }

  useEffect(() => {
    if (gridLoading) {
      retireveSiteUsed();
      retrieveSupplyInventory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, gridLoading, sortParams, invCovDaysConfig]);

  useEffect(() => {
    if (gridLoading) {
      initPageToRetrieve();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterValue, periodList, periodEndDate, itemSearch.current, isSiteGroup]);

  useEffect(() => {
    if (columnDefs === null || columnDefs?.isEmpty) {
      return;
    }

    const columns = cloneDeep(GridUtils.getColumnDefs(columnDefs)).filter(colDef => colDef.hide !== true)
    setCsvColDef(columns)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnDefs])

  return (
    <>
      <FilterContainer>
        <IPLocationTreeFilter viewName={viewName} filterValue={filterValue?.locations?.map(location => location?.locCd)} onChangeFilter={setFilterValue} />
        <ItemHrchyTreeFilter viewName={viewName} onChangeFilter={setFilterValue} enableMultiSelect selectLeafs />
        <StaticAnalysisPeriodComponent title={title} periodList={periodList} setPeriodList={setPeriodList} />
        <LocalizationProvider dateAdapter={AdapterDateFns} >
          <DatePicker
            label={t("periodEnd")}
            views={[YEAR, MONTH, DAY]}
            format={YYYYMMDD}
            openTo={DAY}
            value={periodEndDate}
            maxDate={END_DATE_MAX}
            onAccept={onAcceptHandler}
            slotProps={{ textField: { InputProps: { color: 'primary', size: "small", sx: { mr: 1, width: 160 } } } }}
          />
        </LocalizationProvider>
        <IPItemAutocomplete retrieve={initPageToRetrieve} searchValue={itemSearch} extraCodesRef={extraItemCodesRef} />
        <Box sx={{ ml: "auto" }}>
        {isSiteUsed && <Tooltip className={"wrap-site-icon"} title={isSiteGroup ? t("location") : t("site")}>
          <Box component={"span"}>
            <IconButton aria-label={"site"} color="info" size="small" onClick={siteGroupHandler}>
                {isSiteGroup ? <LocationOnIcon /> : <ShareLocationIcon />}
            </IconButton>
          </Box>
        </Tooltip>}
          <SettingsButton label="daysOfSupplySetting" size="medium" onClick={openInvCovDaysModal} />
          <ExcelExportButton
          title={title}
          service={supplyInventoryService}
          columnDefs={csvColDef}
          setExcelData={setGridDatas}
          params={getRequestParams()} />
        </Box>
      </FilterContainer>
        <Box className="ag-theme-balham" sx={{ height: "100%", mt: 1, mb: 1 }}>
          <AgGridReact
            ref={gridRef}
            columnDefs={GridUtils.getColumnDefs(columnDefs)}
            rowData={invDaysOfsupply}
            {...defaultGridMemo}
            onGridReady={onGridReady}
            onSortChanged={params => setSortParams(GridUtils.getSortState(params))}
          />
        </Box>
      <PaginationContainer
        currentPage={currentPage} totalPages={tPages}
        pageSize={pageSize} pageSizes={pageSizes}
        setCurrentPage={setCurrentPage} setPageSize={setPageSize}
      />
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
      <InvCovDaysSettings
        open={invCovModalOpen}
        setOpen={setInvCovModalOpen}
        title={t("daysOfSupplySetting")}
        applyInvCovDays={setInvCovDaysConfig}
        viewName={viewName}
      />
    </>
  );
}

const DAYS_CASE_01 = 7;
const DAYS_CASE_02 = 14;
const DAYS_CASE_03 = 30;
const DAYS_CASE_04 = 60;
const DAYS_CASE_05= 90;
const DEFAULT_ANALYSIS_PERIOD = [DAYS_CASE_01];
const StaticAnalysisPeriodComponent = ({periodList, setPeriodList}) => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const [isFocused, setIsFocused] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState(periodList);

  const inputRef = useRef();

  const handleClick = (event) => {
    inputRef.current.focus()

    setAnchorEl(event.currentTarget);
  };

  const handleMouseEnter = () => {
    setIsFocused(true);
  };

  const handleMouseLeave = () => {
    setIsFocused(false);
  };

  const handleClose = () => {
    setPeriodList(selectedPeriod);
    setAnchorEl(null);
  };

  const onClearHandler = () => {
    setPeriodList(DEFAULT_ANALYSIS_PERIOD);
    setSelectedPeriod(DEFAULT_ANALYSIS_PERIOD);
  }

  const onCheckBoxHandler = (event) => {
    const name = event.target.value;
    const period = Number(name);
    const checkedValue = event.target.checked;

    if (checkedValue) {
      const addAndSortPeriod = [...selectedPeriod, period];
      addAndSortPeriod.sort((a, b) => a - b);

      setSelectedPeriod(addAndSortPeriod);
    } else {
      const filterPeriod = selectedPeriod.filter(value => value !== period);
      if (filterPeriod.length === 0) {
        filterPeriod.push(DAYS_CASE_01);
      }
      setSelectedPeriod(filterPeriod);
    }
  }

  const isCheckValue = (value) => {
    return selectedPeriod.includes(value);
  }

  return (
    <>
      <FormControl
        className={"analysis-period-filter"}
        size="small"
        sx={{ mr: 1, minWidth: 140, width: 160 }}
      >
        <TextField
          size="small"
          label={t("analysisPeriod")}
          type="string"
          onClick={(e) => handleClick(e)}
          onMouseEnter={() => handleMouseEnter()}
          onMouseLeave={() => handleMouseLeave()}
          value={periodList}
          inputRef={inputRef}
          autoComplete="off"
          InputProps={{
            endAdornment: periodList?.length > 0 ? (
              <>
                {isFocused ? <IconButton size="small" aria-label="clear text" onClick={(e) => {
                  e.stopPropagation(); // 부모 onClick 호출 방지
                  onClearHandler();
                }}>
                  <Clear sx={{ fontSize: '1.2rem' }} />
                </IconButton> : null}
                <InputAdornment position="end">
                  <IconButton aria-label="search" sx={{ padding: 0 }} size="small">{open ? <ArrowDropUp />
                    : <ArrowDropDown />}</IconButton>
                </InputAdornment>
              </>
            ) : <InputAdornment position="end">
              <IconButton aria-label="search" size="small" >{open ? <ArrowDropUp />
                : <ArrowDropDown />}</IconButton>
            </InputAdornment>
          }}
        />
      </FormControl>
      <Popover open={Boolean(open)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        disableAutoFocus
        disableEnforceFocus
        TransitionProps={{ timeout: 0 }}
      >
        <Box>
          <Box sx={{ minWidth: 140, width: 160, overflowY: "auto", overflowX: "hidden" }}>
            <FormControl component="fieldset">
              <FormGroup
                sx={{ ml: 1 }}
              >
                <FormControlLabel checked={isCheckValue(DAYS_CASE_01)} value={DAYS_CASE_01} control={<Checkbox />} label={DAYS_CASE_01} onChange={onCheckBoxHandler}/>
                <FormControlLabel checked={isCheckValue(DAYS_CASE_02)} value={DAYS_CASE_02} control={<Checkbox />} label={DAYS_CASE_02} onChange={onCheckBoxHandler}/>
                <FormControlLabel checked={isCheckValue(DAYS_CASE_03)} value={DAYS_CASE_03} control={<Checkbox />} label={DAYS_CASE_03} onChange={onCheckBoxHandler}/>
                <FormControlLabel checked={isCheckValue(DAYS_CASE_04)} value={DAYS_CASE_04} control={<Checkbox />} label={DAYS_CASE_04} onChange={onCheckBoxHandler}/>
                <FormControlLabel checked={isCheckValue(DAYS_CASE_05)} value={DAYS_CASE_05} control={<Checkbox />} label={DAYS_CASE_05} onChange={onCheckBoxHandler}/>
              </FormGroup>
            </FormControl>
          </Box>
          <Box>
            <Divider sx={{ backgroundColor: "primary.main", borderColor: "primary.main", borderWidth: 0.5, opacity: 0.2 }} />
            <Box
              sx={{
                padding: '8px',
                display: 'flex',
                justifyContent: 'flex-end'
              }}>
              <Button
                sx={{ mr: 1.1 }}
                variant="contained"
                startIcon={<DoneAll />}
                size="small"
                color="primary"
                onClick={handleClose} >
                {t("confirm")}
              </Button>
            </Box>
          </Box>
        </Box>
      </Popover>
    </>
  )
}

export default withTranslation()(SupplyInventory);
